<template>
  <div>
    <vonap-window v-model="showDealForm" width="100%" height="100%" :modal="true">
      <deal-form
        v-if="showDealForm"
        :deal-id="dealId"
        :is-read-only="isReadOnly"
        :modal-visible="showDealForm"
        :isDialog="false"
        @close="showDealForm = false"
      />
    </vonap-window>
    <drawdown-form
      v-if="drawdownModalVisible"
      :value="drawdownModalVisible"
      :selected-operations="selectedOperations"
      :as-of-date="asOfDate"
      @success="refreshGrid"
      @input="drawdownModalVisible = $event"
    />

    <schedule-delivery-form
      v-if="scheduleModalVisible"
      :operation-id="operationId"
      :is-read-only="isReadOnly"
      :schedule-modal-visible="scheduleModalVisible"
      @close="closeScheduleDeliveryModal"
    />
    <component
      :is="component"
      width="1200px"
      height="500px"
      title="Exposure Drilldown"
      @close="$emit('input', false)"
      v-model="value"
      v-if="value"
    >
      <page-toolbar>
        <slot />
        <kendo-button icon="excel" title="Export to excel" @click="exportToExcel"></kendo-button>
        <kendo-button title="Drawdown" @click="getSelectedRows">
          <i class="fas fa-chart-line"></i>
        </kendo-button>
        <kendo-button title="Close / Back" @click="$emit('input', false)">
          <i class="fa fa-times"></i>
        </kendo-button>
      </page-toolbar>
      <kendo-grid
        :data-source="dataSource"
        v-on:databound="onGridDataBound"
        ref="exposureDrillDownGrid"
        :height="component === 'div' ? 'calc(100vh - 215px)' : '420px'"
      >
        <kendo-grid-column
          :selectable="true"
          :attributes="{class: 'selectableCol'}"
          :locked="true"
          :width="50"
        >
        </kendo-grid-column>
        <kendo-grid-column
          title=""
          :width="50"
          :attributes="{style: 'text-align: left;'}"
          @template="openDealTemplate"
          :locked="true"
          :lockable="false"
        />
        <kendo-grid-column
          field="number"
          title="Trade Id"
          width="100px"
          :attributes="{style: 'text-align: left;'}"
          @template="deliveryScheduleLinkTemplate"
          :locked="true"
          :lockable="false"
        />
        <kendo-grid-column field="sector" title="Sector" :width="150" />
        <kendo-grid-column field="division" title="Division" width="150px" />
        <kendo-grid-column field="counterparty" title="Counterparty" width="100px" />
        <kendo-grid-column field="reference" title="Reference" :width="180" />
        <kendo-grid-column field="description" title="Description" width="300px" />
        <kendo-grid-column field="product" title="Product" width="100px" />
        <kendo-grid-column field="leg" title="Leg" width="100px" />
        <kendo-grid-column
          field="tradeDate"
          title="Trade Date"
          format="{0:yyyy-MM-dd}"
          width="100px"
          :attributes="{style: 'text-align: right;'}"
        />
        <kendo-grid-column
          field="startDate"
          title="Start Date"
          format="{0:yyyy-MM-dd}"
          width="100px"
          :attributes="{style: 'text-align: right;'}"
        />
        <kendo-grid-column
          field="hedgeMonth"
          title="Hedge month"
          format="{0:yyyy-MM-dd}"
          :width="120"
          :attributes="{style: 'text-align: right;'}"
        />
        <kendo-grid-column
          field="maturityDate"
          title="Maturity Date"
          format="{0:yyyy-MM-dd}"
          width="120px"
          :attributes="{style: 'text-align: right;'}"
        />
        <kendo-grid-column
          field="settlementDate"
          title="Settlement"
          format="{0:yyyy-MM-dd}"
          width="120px"
          :attributes="{style: 'text-align: right;'}"
        />
        <kendo-grid-column field="buySell" title="Buy/Sell" width="75px" />
        <kendo-grid-column
          field="undeliveredAmount"
          title="Undelivered Amount"
          format="{0:##,#;##,#}"
          width="120px"
          :attributes="{class: 'numericColumn'}"
          :aggregates="['sum']"
          footerTemplate="<div style='text-align: right'>#=kendo.toString(data.undeliveredAmount.sum, '\#\#,\#;\#\#,\#') # </div>"
        />
        <kendo-grid-column
          field="hedgeAmount"
          title="Quantity"
          width="120px"
          :attributes="{class: 'numericColumn'}"
          :aggregates="['sum']"
          template="<div style='text-align: right'>#=kendo.toString(Math.abs(data.hedgeAmount), 'n0') # </div>"
        />
        <kendo-grid-column
          field="amountCcy1"
          title="Quantity"
          format="{0:N0}"
          width="120px"
          :hidden="true"
          :attributes="{class: 'numericColumn'}"
          :aggregates="['sum']"
        />
        <kendo-grid-column field="ccy1" title="Com.Ccy" width="80px" />
        <kendo-grid-column
          field="hedgeRate"
          title="Contract Rate"
          format="{0:N4}"
          width="100px"
          :attributes="{class: 'numericColumn'}"
        />
        <kendo-grid-column
          field="amountCcy2"
          title="Amount Ccy"
          format="{0:N0}"
          width="120px"
          :attributes="{class: 'numericColumn'}"
        />
        <kendo-grid-column field="ccy2" title="Ccy" width="75px" />
        <kendo-grid-column
          field="hedgeType"
          title="Hedge Type"
          :width="100"
          :attributes="{style: 'text-align: left;'}"
        />
        <kendo-grid-column
          field="isExercised"
          title="Exercised"
          :width="90"
          :attributes="{style: 'text-align: left;'}"
        />
        <kendo-grid-column
          title="Reference Rate"
          field="referenceRate"
          :width="140"
          :attributes="{style: 'text-align: right;'}"
          format="{0:n4}"
        />
        <kendo-grid-column
          title="Forward Points"
          field="forwardPoints"
          :width="140"
          :attributes="{style: 'text-align: right;'}"
          format="{0:n4}"
        />
        <kendo-grid-column
          field="gainLoss"
          title="Gain/Loss"
          :width="140"
          :attributes="{style: 'text-align: right;'}"
          format="{0:n2}"
        />
        <kendo-grid-column
          field="optionType"
          title="Option"
          :width="100"
          :attributes="{style: 'text-align: left;'}"
        />
        <kendo-grid-column
          field="otherBarrier"
          title="Other Barrier"
          format="{0:n2}"
          :width="80"
          :attributes="{style: 'text-align: right;'}"
        />
        <kendo-grid-column field="settlementMethod" title="Settlement Method" width="90px" />
        <kendo-grid-column field="settlementCcy" title="Settlement Ccy" width="100px" />
        <kendo-grid-column
          field="lastModified"
          title="Last Modified"
          format="{0:yyyy-MM-dd hh:mm:ss}"
          width="180px"
        />
        <kendo-grid-column field="lastUser" title="Last User" width="150px" />
      </kendo-grid>
    </component>
  </div>
</template>

<script>
import VonapWindow from '@/components/VonapWindow'
import PageToolbar from '@/components/PageToolbar'
import DealForm from '../../views/List/Deal/DealForm'
import ScheduleDeliveryForm from '../../views/List/Deal/ScheduleDeliveryForm'
import DrawdownForm from '../../views/List/Deal/DrawdownForm'
import params from '../../helpers/params'
import notification from '@/helpers/notification'
import {mapGetters} from 'vuex'

export default {
  props: {
    value: {
      type: Boolean,
    },
    reportParameters: {
      type: Object,
    },
    isReadOnly: {type: Boolean, default: true},
    readUrl: {type: String, required: true},
    exportUrl: {type: String, required: true},
    component: {type: String, required: true},
  },
  mounted() {
    document.body.addEventListener('click', this.dealLinkClick, false)
  },
  beforeDestroy() {
    document.body.removeEventListener('click', this.dealLinkClick)
  },
  data() {
    return {
      dealId: 0,
      showDealForm: false,
      scheduleModalVisible: false,
      drawdownModalVisible: false,
      selectedOperations: [],
      operationId: null,
    }
  },
  computed: {
    dataSource() {
      return {
        transport: {
          read: {
            type: 'GET',
            url: this.readUrl,
            data: this.reportParameters,
          },
        },
        schema: {
          model: {
            id: 'number',
            fields: {
              number: {type: 'string'},
              operationId: {type: 'number'},
              status: {type: 'string'},
              sector: {type: 'string'},
              hedgeType: {type: 'string'},
              division: {type: 'string'},
              counterparty: {type: 'string'},
              leg: {type: 'string'},
              product: {type: 'string'},
              startDate: {type: 'date'},
              tradeDate: {type: 'date'},
              settlementDate: {type: 'date'},
              maturityDate: {type: 'date'},
              hedgeMonth: {type: 'date'},
              buySell: {type: 'string'},
              undeliveredAmount: {type: 'number'},
              amountCcy1: {type: 'number'},
              hedgeAmount: {type: 'number'},
              gainLoss: {type: 'number'},
              ccy1: {type: 'string'},
              hedgeRate: {type: 'string'},
              collarRate: {type: 'string'},
              amountCcy2: {type: 'number'},
              otherBarrier: {type: 'number'},
              ccy2: {type: 'string'},
              description: {type: 'string'},
              reference: {type: 'string'},
              isExercised: {type: 'string'},
              lastModified: {type: 'date'},
              lastUser: {type: 'string'},
              isFuture: {type: 'bool'},
              canDrawDown: {type: 'bool'},
            },
          },
        },
        pageSize: 0,
        aggregate: [{field: 'undeliveredAmount', aggregate: 'sum'}],
      }
    },
    ...mapGetters({tokenString: 'authentication/tokenString'}),
    asOfDate() {
      return this.reportParameters.asOfDate
    },
  },
  methods: {
    refreshGrid: function() {
      this.drawdownModalVisible = false
      const grid = this.$refs.exposureDrillDownGrid.kendoWidget()
      grid.dataSource.read()
    },
    onGridDataBound(e) {
      var gridData = e.sender.dataSource.view()
      var grid = this.$refs.exposureDrillDownGrid.kendoWidget()
      gridData.forEach(dataItem => {
        var row = grid.lockedTable.find("tr[data-uid='" + dataItem.uid + "']")
        if (!dataItem.canDrawDown) {
          row.find('.k-checkbox').addClass('hidden')
          row.find('.k-checkbox-label').addClass('hidden')
        }
      })
    },
    getSelectedRows() {
      const grid = this.$refs.exposureDrillDownGrid.kendoWidget()
      const selectedIds = grid.selectedKeyNames().map(id => +id)
      if (selectedIds.length == 0) {
        notification('Please select one', 'error')
      } else {
        this.selectedOperations = selectedIds
        this.drawdownModalVisible = true
      }
    },
    openDealTemplate(item) {
      return `<a title="Open Deal" data-dealid="${
        item.dealId
      }" href="javascript:;" class="dealCodeLink fas fa-external-link-alt" >
      </a>`
    },
    deliveryScheduleLinkTemplate(item) {
      if (item.isFuture) {
        return `<strong> ${item.number} </strong>`
      } else {
        return `<a title="Delivery Schedule" data-operationid="${
          item.operationId
        }" href="javascript:;" class="deliveryScheduleLink k-cellLink" >
            ${item.number}
        </a>`
      }
    },
    openDealForm(dealId) {
      this.dealId = dealId
      this.showDealForm = true
    },
    exportToExcel() {
      const exportUrl = this.exportUrl
      const qs = params.fromObject({...this.reportParameters, t: this.tokenString})
      location.href = `${exportUrl}?${qs}`
    },
    dealLinkClick({target}) {
      if (/dealCodeLink/.test(target.className)) {
        const dealId = target.dataset.dealid
        this.openDealForm(dealId)
      } else if (/deliveryScheduleLink/.test(target.className)) {
        const operationId = target.dataset.operationid
        this.operationId = operationId
        this.scheduleModalVisible = true
      }
    },
    closeScheduleDeliveryModal() {
      this.operationId = null
      this.scheduleModalVisible = false
    },
    closeDrawdownModal() {
      this.drawdownModalVisible = false
    },
  },
  components: {
    VonapWindow,
    PageToolbar,
    DealForm,
    DrawdownForm,
    ScheduleDeliveryForm,
  },
}
</script>
